import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { rectSortingStrategy, SortableContext, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { Stack, Typography, useTheme } from '@mui/material';
import { useAtomValue } from 'jotai';
import { merge } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { KpiSection } from '../../../data-models/company-financials.data-model';
import { RendererType } from '../../../data-models/field-definition';
import { IField } from '../../../data-models/field2.data-model';
import {
  IKPITableDataModel,
  IKPITemplate,
  IKPITemplateSectionDataModel,
} from '../../../data-models/kpi-template.data-model';
import { isTextualKpiField, universalAndSystemKPIAtom } from '../../../services/state/KPIConfigState';
import { KPITemplateBuildMenu } from './KPITemplateBuildMenu';
import { createKPIItemByType } from './KPITemplateFactory';
import { getTextualKpiOptions, useTextualKpiFields } from './KpiTemplateTextualFieldUtils';
import { disableActual } from './Sections/FormPreviewItem/KPITableConfig';
import { SectionItem } from './Sections/SectionItem';

export function KPITemplateFormBuilder() {
  const { colors } = useTheme();
  const { fields: sections, append, remove, swap } = useFieldArray<IKPITemplate>({ name: 'sections' });
  const universalAndSystemKPI = useAtomValue(universalAndSystemKPIAtom);
  const nonTextualUniversalKpis = useMemo(() => {
    if (!universalAndSystemKPI) {
      return [];
    }
    return universalAndSystemKPI.filter((kpi) => !isTextualKpiField(kpi));
  }, [universalAndSystemKPI]);

  const { control } = useFormContext();
  const existingSections = useWatch({ name: 'sections', control }) as IKPITemplateSectionDataModel[];
  const { availableTextualKpis, disableTextualKpis } = useTextualKpiFields(existingSections);

  const disableActualSection = useMemo(() => disableActual(existingSections), [existingSections]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const onAddSectionItem = useCallback(
    (type: string) => {
      const section = createKPIItemByType(type, nonTextualUniversalKpis, availableTextualKpis);
      let sectionType;
      if (type === RendererType.kpiTable || type === RendererType.universalKpiTable) {
        sectionType = disableActualSection ? KpiSection.budget : KpiSection.actual;
      }
      append(
        merge(section, {
          meta: {
            ...section.meta,
            section: sectionType,
          },
        })
      );
    },
    [append, availableTextualKpis, disableActualSection, nonTextualUniversalKpis]
  );

  const onRemoveSectionItem = useCallback(
    (index: number) => {
      remove(index);
    },
    [remove]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (active.id !== over?.id) {
        const sectionsIds = sections.map((section) => section.id);
        const oldIndex = sectionsIds.findIndex((id) => id === active.id);
        const newIndex = sectionsIds.findIndex((id) => id === over?.id);

        swap(newIndex, oldIndex);
      }
    },
    [sections, swap]
  );

  return (
    <Stack width={'100%'}>
      <Stack width={'100%'} direction='row' gap='1rem' sx={{ marginBottom: '1rem' }}>
        <Stack width={'50%'}>
          <Typography variant='body1' color={colors.primary[100]} fontWeight={700}>
            Building
          </Typography>
        </Stack>
        <Typography variant='body1' color={colors.primary[100]} fontWeight={700}>
          Preview
        </Typography>
      </Stack>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToVerticalAxis]}
      >
        <SortableContext items={sections} strategy={rectSortingStrategy}>
          {sections.map((section, index) => {
            const sectionRef = `sections.${index}`;
            return (
              <SectionItem
                sectionId={section.id}
                sectionRef={sectionRef}
                key={sectionRef}
                onRemoveSection={() => onRemoveSectionItem(index)}
                textualKpiOptions={getTextualKpiOptions(section, availableTextualKpis)}
              />
            );
          })}
        </SortableContext>
      </DndContext>
      <KPITemplateBuildMenu
        onAddSectionItem={onAddSectionItem}
        disableKpis={hasBothMetricsSections(sections)}
        disableTextualKpis={disableTextualKpis}
      />
    </Stack>
  );
}

export function hasBothMetricsSections(sections: (Partial<IField<unknown>> | IKPITableDataModel)[]) {
  return sections.filter(isKpiTableDataModel).length > 1;
}

function isKpiTableDataModel(
  section: Partial<IField<unknown>> | IKPITableDataModel
): section is IKPITableDataModel {
  return 'meta' in section && (section?.meta as IKPITableDataModel)?.metrics != null;
}
